/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Component, OnInit, EventEmitter, Output, Input} from '@angular/core';
import * as go from 'gojs';
import {ActivatedRoute} from "@angular/router";
import {HealthMonitorService} from "../../services/health-monitor.service";
import {HealthData} from "../../models/healthData";

const $$ = go.GraphObject.make;
declare var $: any;

@Component({
  selector: 'app-health-monitor',
  templateUrl: './health-monitor.component.html'
})

export class HealthMonitorComponent implements OnInit {
  static that: HealthMonitorComponent;

  responseData: HealthData = null;
  websiteDiagram;

  constructor(private healthMonitorService: HealthMonitorService) {
  }

  ngOnInit(): void {
    HealthMonitorComponent.that = this;


    this.healthMonitorService.getUptimeDataForServer("1").then(
      response => {
        this.responseData = response;
        console.log("ip address is " + this.responseData.pingWeekPercentage);
        this.executeGoJS();
        this.init();
      },
      error => {
        console.log("ERROR OCCURRED!");
      }
    );

  }


  init(): void {
    //if (window.goSamples) goSamples();  // init for these samples -- you don't need to call this
    //const $ = go.GraphObject.make;  // for conciseness in defining templates

    this.websiteDiagram =
      $$(go.Diagram, "websiteDiagramDiv",  // must name or refer to the DIV HTML element
        {
          grid: $$(go.Panel, "Grid",
            $$(go.Shape, "LineH", {stroke: "lightgray", strokeWidth: 0.5}),
            $$(go.Shape, "LineH", {stroke: "gray", strokeWidth: 0.5, interval: 10}),
            $$(go.Shape, "LineV", {stroke: "lightgray", strokeWidth: 0.5}),
            $$(go.Shape, "LineV", {stroke: "gray", strokeWidth: 0.5, interval: 10})
          ),
          "draggingTool.dragsLink": true,
          "draggingTool.isGridSnapEnabled": true,
          "linkingTool.isUnconnectedLinkValid": true,
          "linkingTool.portGravity": 20,
          "relinkingTool.isUnconnectedLinkValid": true,
          "relinkingTool.portGravity": 20,
          "relinkingTool.fromHandleArchetype":
            $$(go.Shape, "Diamond", {segmentIndex: 0, cursor: "pointer", desiredSize: new go.Size(8, 8), fill: "tomato", stroke: "darkred"}),
          "relinkingTool.toHandleArchetype":
            $$(go.Shape, "Diamond", {segmentIndex: -1, cursor: "pointer", desiredSize: new go.Size(8, 8), fill: "darkred", stroke: "tomato"}),
          "linkReshapingTool.handleArchetype":
            $$(go.Shape, "Diamond", {desiredSize: new go.Size(7, 7), fill: "lightblue", stroke: "deepskyblue"}),
          "rotatingTool.handleAngle": 270,
          "rotatingTool.handleDistance": 30,
          "rotatingTool.snapAngleMultiple": 15,
          "rotatingTool.snapAngleEpsilon": 15,
          "undoManager.isEnabled": true
        });

    // when the document is modified, add a "*" to the title and enable the "Save" button
    this.websiteDiagram.addDiagramListener("Modified", function (): void {
      const button: any = $("#SaveButton");
      if (button) {
        button.disabled = !HealthMonitorComponent.that.websiteDiagram.isModified;
      }
      const idx = document.title.indexOf("*");
      if (HealthMonitorComponent.that.websiteDiagram.isModified) {
        if (idx < 0) {
          document.title += "*";
        }
      } else {
        if (idx >= 0) {
          document.title = document.title.substr(0, idx);
        }
      }
    });

    // Define a function for creating a "port" that is normally transparent.
    // The "name" is used as the GraphObject.portId, the "spot" is used to control how links connect
    // and where the port is positioned on the node, and the boolean "output" and "input" arguments
    // control whether the user can draw links from or to the port.
    function makePort(name: any, spot: any, output: any, input: any): any {
      // the port is basically just a small transparent square
      return $$(go.Shape, "Circle",
        {
          fill: null,  // not seen, by default; set to a translucent gray by showSmallPorts, defined below
          stroke: null,
          desiredSize: new go.Size(7, 7),
          alignment: spot,  // align the port on the main Shape
          alignmentFocus: spot,  // just inside the Shape
          portId: name,  // declare this object to be a "port"
          fromSpot: spot, toSpot: spot,  // declare where links may connect at this port
          fromLinkable: output, toLinkable: input,  // declare whether the user may draw links to/from here
          cursor: "pointer"  // show a different cursor to indicate potential link point
        });
    }

    const nodeSelectionAdornmentTemplate =
      $$(go.Adornment, "Auto",
        $$(go.Shape, {fill: null, stroke: "deepskyblue", strokeWidth: 1.5, strokeDashArray: [4, 2]}),
        $$(go.Placeholder)
      );

    const nodeResizeAdornmentTemplate =
      $$(go.Adornment, "Spot",
        {locationSpot: go.Spot.Right},
        $$(go.Placeholder),
        $$(go.Shape, {alignment: go.Spot.TopLeft, cursor: "nw-resize", desiredSize: new go.Size(6, 6), fill: "lightblue", stroke: "deepskyblue"}),
        $$(go.Shape, {alignment: go.Spot.Top, cursor: "n-resize", desiredSize: new go.Size(6, 6), fill: "lightblue", stroke: "deepskyblue"}),
        $$(go.Shape, {alignment: go.Spot.TopRight, cursor: "ne-resize", desiredSize: new go.Size(6, 6), fill: "lightblue", stroke: "deepskyblue"}),

        $$(go.Shape, {alignment: go.Spot.Left, cursor: "w-resize", desiredSize: new go.Size(6, 6), fill: "lightblue", stroke: "deepskyblue"}),
        $$(go.Shape, {alignment: go.Spot.Right, cursor: "e-resize", desiredSize: new go.Size(6, 6), fill: "lightblue", stroke: "deepskyblue"}),
        $$(go.Shape, {alignment: go.Spot.BottomLeft, cursor: "se-resize", desiredSize: new go.Size(6, 6), fill: "lightblue", stroke: "deepskyblue"}),
        $$(go.Shape, {alignment: go.Spot.Bottom, cursor: "s-resize", desiredSize: new go.Size(6, 6), fill: "lightblue", stroke: "deepskyblue"}),
        $$(go.Shape, {alignment: go.Spot.BottomRight, cursor: "sw-resize", desiredSize: new go.Size(6, 6), fill: "lightblue", stroke: "deepskyblue"})
      );

    const nodeRotateAdornmentTemplate =
      $$(go.Adornment,
        {locationSpot: go.Spot.Center, locationObjectName: "CIRCLE"},
        $$(go.Shape, "Circle", {name: "CIRCLE", cursor: "pointer", desiredSize: new go.Size(7, 7), fill: "lightblue", stroke: "deepskyblue"}),
        $$(go.Shape, {geometryString: "M3.5 7 L3.5 30", isGeometryPositioned: true, stroke: "deepskyblue", strokeWidth: 1.5, strokeDashArray: [4, 2]})
      );

    this.websiteDiagram.nodeTemplate =
      $$(go.Node, "Spot",
        {locationSpot: go.Spot.Center},
        new go.Binding("location", "loc", go.Point.parse).makeTwoWay(go.Point.stringify),
        {selectable: true, selectionAdornmentTemplate: nodeSelectionAdornmentTemplate},
        {resizable: true, resizeObjectName: "PANEL", resizeAdornmentTemplate: nodeResizeAdornmentTemplate},
        {rotatable: true, rotateAdornmentTemplate: nodeRotateAdornmentTemplate},
        new go.Binding("angle").makeTwoWay(),
        // the main object is a Panel that surrounds a TextBlock with a Shape
        $$(go.Panel, "Auto",
          {name: "PANEL"},
          new go.Binding("desiredSize", "size", go.Size.parse).makeTwoWay(go.Size.stringify),
          $$(go.Shape, "Rectangle",  // default figure
            {
              portId: "", // the default port: if no spot on link data, use closest side
              fromLinkable: true, toLinkable: true, cursor: "pointer",
              fill: "white",  // default color
              strokeWidth: 2
            },
            new go.Binding("figure"),
            new go.Binding("fill")),
          $$(go.TextBlock,
            {
              font: "bold 11pt Helvetica, Arial, sans-serif",
              margin: 8,
              maxSize: new go.Size(160, NaN),
              wrap: go.TextBlock.WrapFit,
              editable: true
            },
            new go.Binding("text").makeTwoWay())
        ),
        // four small named ports, one on each side:
        makePort("T", go.Spot.Top, false, true),
        makePort("L", go.Spot.Left, true, true),
        makePort("R", go.Spot.Right, true, true),
        makePort("B", go.Spot.Bottom, true, false),
        { // handle mouse enter/leave events to show/hide the ports
          mouseEnter: function (e: any, node: any): void {
            showSmallPorts(node, true);
          },
          mouseLeave: function (e: any, node: any): void {
            showSmallPorts(node, false);
          }
        }
      );

    function showSmallPorts(node: any, show: any): void {
      node.ports.each(function (port: any): void {
        if (port.portId !== "") {  // don't change the default port, which is the big shape
          port.fill = show ? "rgba(0,0,0,.3)" : null;
        }
      });
    }

    const linkSelectionAdornmentTemplate =
      $$(go.Adornment, "Link",
        $$(go.Shape,
          // isPanelMain declares that this Shape shares the Link.geometry
          {isPanelMain: true, fill: null, stroke: "deepskyblue", strokeWidth: 0})  // use selection object's strokeWidth
      );


    this.websiteDiagram.linkTemplate =
      $$(go.Link,  // the whole link panel
        {selectable: true, selectionAdornmentTemplate: linkSelectionAdornmentTemplate},
        {relinkableFrom: true, relinkableTo: true, reshapable: true},
        {
          routing: go.Link.AvoidsNodes,
          curve: go.Link.JumpOver,
          corner: 5,
          toShortLength: 4
        },
        new go.Binding("points").makeTwoWay(),
        $$(go.Shape,  // the link path shape
          {isPanelMain: true, strokeWidth: 2, stroke: "yellow"}),
        $$(go.Shape,  // the arrowhead
          {toArrow: "Standard", stroke: "yellow"}),
        $$(go.Panel, "Auto",
          new go.Binding("visible", "isSelected").ofObject(),
          $$(go.Shape, "RoundedRectangle",  // the link shape
            {fill: "#F8F8F8", stroke: "yellow"}),
          $$(go.TextBlock,
            {
              textAlign: "center",
              font: "10pt helvetica, arial, sans-serif",
              stroke: "#919191",
              margin: 2,
              minSize: new go.Size(10, NaN),
              editable: true
            },
            new go.Binding("text").makeTwoWay())
        )
      );


    this.load();  // load an initial diagram from some JSON text

    // initialize the Palette that is on the left side of the page
    const Palette =
      $$(go.Palette, "myPaletteDiv",  // must name or refer to the DIV HTML element
        {
          maxSelectionCount: 1,
          nodeTemplateMap: this.websiteDiagram.nodeTemplateMap,  // share the templates used by websiteDiagram
          linkTemplate: // simplify the link template, just in this Palette
            $$(go.Link,
              { // because the GridLayout.alignment is Location and the nodes have locationSpot == Spot.Center,
                // to line up the Link in the same manner we have to pretend the Link has the same location spot
                locationSpot: go.Spot.Center,
                selectionAdornmentTemplate:
                  $$(go.Adornment, "Link",
                    {locationSpot: go.Spot.Center},
                    $$(go.Shape,
                      {isPanelMain: true, fill: null, stroke: "deepskyblue", strokeWidth: 0}),
                    $$(go.Shape,  // the arrowhead
                      {toArrow: "Standard", stroke: null})
                  )
              },
              {
                routing: go.Link.AvoidsNodes,
                curve: go.Link.JumpOver,
                corner: 5,
                toShortLength: 4
              },
              new go.Binding("points"),
              $$(go.Shape,  // the link path shape
                {isPanelMain: true, strokeWidth: 2}),
              $$(go.Shape,  // the arrowhead
                {toArrow: "Standard", stroke: null})
            ),
          model: new go.GraphLinksModel([  // specify the contents of the Palette
            {text: "Trigger", figure: "Circle", fill: "#00AD5F"},
            {text: "Function"},
            {text: "Service", figure: "Database", fill: "lightgray"},
            {text: "Condition", figure: "Diamond", fill: "lightskyblue"},
            {text: "Reaction", figure: "Circle", fill: "#CE0620"},
            {text: "Comment", figure: "RoundedRectangle", fill: "lightyellow"}
          ], [
            // the Palette also has a disconnected Link, which the user can drag-and-drop
            {
              color: "white", points: new go.List(/* go.Point */).addAll([new go.Point(0, 0),
                new go.Point(30, 0), new go.Point(30, 40), new go.Point(60, 40)])
            }
          ])
        });
  }


  // Show the diagram's model in JSON format that the user may edit
  save(): void {
    this.saveDiagramProperties();  // do this first, before writing to JSON
    $("#mySavedModel").value = this.websiteDiagram.model.toJson();
    this.websiteDiagram.isModified = false;
  }

  load(): void {
    this.websiteDiagram.model = go.Model.fromJson($("#mySavedModel").val());
    console.log("Data loaded: " + $("#mySavedModel").val());
    //console.log("Data loaded: " + jsonData);
    //WorkflowComponent.that.websiteDiagram.model = go.Model.fromJson(jsonData);
    HealthMonitorComponent.that.loadDiagramProperties();  // do this after the Model.modelData has been brought into memory
  }

  saveDiagramProperties(): void {
    this.websiteDiagram.model.modelData.position = go.Point.stringify(this.websiteDiagram.position);
  }

  loadDiagramProperties(): void {
    // set Diagram.initialPosition, not Diagram.position, to handle initialization side-effects
    const pos = this.websiteDiagram.model.modelData.position;
    if (pos) {
      this.websiteDiagram.initialPosition = go.Point.parse(pos);
    }
  }

  executeGoJS(): void {
    this.executeWebsiteDiagram();
    this.executeServerDiagram();
    this.executeDatabaseDiagram();
  }

  executeWebsiteDiagram(): void {
    const hourTicks = Math.ceil(this.responseData.pingHourPercentage) * 3.6;
    const dayTicks = Math.ceil(this.responseData.pingDayPercentage) * 3.6;
    const weekTicks = Math.ceil(this.responseData.pingWeekPercentage) * 3.6;
    const monthTicks = Math.ceil(this.responseData.pingMonthPercentage) * 3.6;

    const hourPercentage = Math.ceil(this.responseData.pingHourPercentage) + "%";
    const dayPercentage = Math.ceil(this.responseData.pingDayPercentage) + "%";
    const weekPercentage = Math.ceil(this.responseData.pingWeekPercentage) + "%";
    const monthPercentage = Math.ceil(this.responseData.pingMonthPercentage) + "%";

    const myDiagram =
      $$(go.Diagram, "websiteDiagramDiv",
        {
          nodeTemplate:
            $$(go.Node, "Vertical",
              $$(go.Panel,
                new go.Binding("itemArray", "slices"),
                {
                  itemTemplate:
                    $$(go.Panel,
                      $$(go.Shape,
                        {fill: "lightgreen", isGeometryPositioned: true},
                        new go.Binding("fill", "color"),
                        new go.Binding("geometry", "", this.makeGeo)),
                      {
                        toolTip:
                          $$("ToolTip",
                            $$(go.TextBlock, {margin: 4},
                              new go.Binding("text", "", function (data) {
                                return data.color + ": " + data.start +
                                  " to " + (data.start + data.sweep);
                              }))
                          )
                      }
                    )
                }),
              $$(go.TextBlock,
                new go.Binding("text"))
            ),
          model: $$(go.GraphLinksModel,
            {
              copiesArrays: true,
              copiesArrayObjects: true,
              nodeDataArray:
                [  // node data
                  {
                    key: 1,
                    text: "192.168.4.140\nUPTIME (1 hour) - " + hourPercentage,
                    slices: [
                      {start: -30, sweep: hourTicks, color: "green"},
                      {start: -30 + hourTicks, sweep: 360-hourTicks, color: "black"}
                    ]
                  },
                  {
                    key: 2,
                    text: "192.168.4.140\nUPTIME (1 day) - " + dayPercentage,
                    slices: [
                      {start: -30, sweep: dayTicks, color: "green"},
                      {start: -30 + dayTicks, sweep: 360-dayTicks, color: "black"}
                    ]
                  },

                  {
                    key: 3,
                    text: "192.168.4.140\nUPTIME (1 week) - " + weekPercentage,
                    slices: [
                      {start: -30, sweep: weekTicks, color: "green"},
                      {start: -30 + weekTicks, sweep: 360-weekTicks, color: "black"}
                    ]
                  },

                  {
                    key: 4,
                    text: "192.168.4.140\nUPTIME (1 month) - " + monthPercentage,
                    slices: [
                      {start: -30, sweep: monthTicks, color: "green"},
                      {start: -30 + monthTicks, sweep: 360-monthTicks, color: "black"}
                    ]
                  }
                ],
              linkDataArray:
                [  // link data
                  //{from: 1, to: 2}
                ]
            })
        }
      );
  }

  executeDatabaseDiagram(): void {
    const hourTicks = Math.ceil(this.responseData.webHourPercentage) * 3.6;
    const dayTicks = Math.ceil(this.responseData.webDayPercentage) * 3.6;
    const weekTicks = Math.ceil(this.responseData.webWeekPercentage) * 3.6;
    const monthTicks = Math.ceil(this.responseData.webMonthPercentage) * 3.6;

    const hourPercentage = Math.ceil(this.responseData.webHourPercentage) + "%";
    const dayPercentage = Math.ceil(this.responseData.webDayPercentage) + "%";
    const weekPercentage = Math.ceil(this.responseData.webWeekPercentage) + "%";
    const monthPercentage = Math.ceil(this.responseData.webMonthPercentage) + "%";

    const myDiagram =
      $$(go.Diagram, "serverDiagramDiv",
        {
          nodeTemplate:
            $$(go.Node, "Vertical",
              $$(go.Panel,
                new go.Binding("itemArray", "slices"),
                {
                  itemTemplate:
                    $$(go.Panel,
                      $$(go.Shape,
                        {fill: "lightgreen", isGeometryPositioned: true},
                        new go.Binding("fill", "color"),
                        new go.Binding("geometry", "", this.makeGeo)),
                      {
                        toolTip:
                          $$("ToolTip",
                            $$(go.TextBlock, {margin: 4},
                              new go.Binding("text", "", function (data) {
                                return data.color + ": " + data.start +
                                  " to " + (data.start + data.sweep);
                              }))
                          )
                      }
                    )
                }),
              $$(go.TextBlock,
                new go.Binding("text"))
            ),
          model: $$(go.GraphLinksModel,
            {
              copiesArrays: true,
              copiesArrayObjects: true,
              nodeDataArray:
                [  // node data
                  {
                    key: 1,
                    text: "192.168.4.200\nUPTIME (1 hour) - " + hourPercentage,
                    slices: [
                      {start: -30, sweep: hourTicks, color: "green"},
                      {start: -30 + hourTicks, sweep: 360-hourTicks, color: "black"}
                    ]
                  },
                  {
                    key: 2,
                    text: "192.168.4.200\nUPTIME (1 day) - " + dayPercentage,
                    slices: [
                      {start: -30, sweep: dayTicks, color: "green"},
                      {start: -30 + dayTicks, sweep: 360-dayTicks, color: "black"}
                    ]
                  },

                  {
                    key: 3,
                    text: "192.168.4.200\nUPTIME (1 week) - " + weekPercentage,
                    slices: [
                      {start: -30, sweep: weekTicks, color: "green"},
                      {start: -30 + weekTicks, sweep: 360-weekTicks, color: "black"}
                    ]
                  },

                  {
                    key: 4,
                    text: "192.168.4.200\nUPTIME (1 month) - " + monthPercentage,
                    slices: [
                      {start: -30, sweep: monthTicks, color: "green"},
                      {start: -30 + monthTicks, sweep: 360-monthTicks, color: "black"}
                    ]
                  }
                ],
              linkDataArray:
                [  // link data
                  //{from: 1, to: 2}
                ]
            })
        }
      );
  }

  executeServerDiagram(): void {
    const hourTicks = Math.ceil(this.responseData.serverHourPercentage) * 3.6;
    const dayTicks = Math.ceil(this.responseData.serverDayPercentage) * 3.6;
    const weekTicks = Math.ceil(this.responseData.serverWeekPercentage) * 3.6;
    const monthTicks = Math.ceil(this.responseData.serverMonthPercentage) * 3.6;

    const hourPercentage = Math.ceil(this.responseData.serverHourPercentage) + "%";
    const dayPercentage = Math.ceil(this.responseData.serverDayPercentage) + "%";
    const weekPercentage = Math.ceil(this.responseData.serverWeekPercentage) + "%";
    const monthPercentage = Math.ceil(this.responseData.serverMonthPercentage) + "%";

    const myDiagram =
      $$(go.Diagram, "databaseDiagramDiv",
        {
          nodeTemplate:
            $$(go.Node, "Vertical",
              $$(go.Panel,
                new go.Binding("itemArray", "slices"),
                {
                  itemTemplate:
                    $$(go.Panel,
                      $$(go.Shape,
                        {fill: "lightgreen", isGeometryPositioned: true},
                        new go.Binding("fill", "color"),
                        new go.Binding("geometry", "", this.makeGeo)),
                      {
                        toolTip:
                          $$("ToolTip",
                            $$(go.TextBlock, {margin: 4},
                              new go.Binding("text", "", function (data) {
                                return data.color + ": " + data.start +
                                  " to " + (data.start + data.sweep);
                              }))
                          )
                      }
                    )
                }),
              $$(go.TextBlock,
                new go.Binding("text"))
            ),
          model: $$(go.GraphLinksModel,
            {
              copiesArrays: true,
              copiesArrayObjects: true,
              nodeDataArray:
                [  // node data
                  {
                    key: 1,
                    text: "192.168.4.140\nUPTIME (1 hour) - " + hourPercentage,
                    slices: [
                      {start: -30, sweep: hourTicks, color: "green"},
                      {start: -30 + hourTicks, sweep: 360-hourTicks, color: "black"}
                    ]
                  },
                  {
                    key: 2,
                    text: "192.168.4.140\nUPTIME (1 day) - " + dayPercentage,
                    slices: [
                      {start: -30, sweep: dayTicks, color: "green"},
                      {start: -30 + dayTicks, sweep: 360-dayTicks, color: "black"}
                    ]
                  },

                  {
                    key: 3,
                    text: "192.168.4.140\nUPTIME (1 week) - " + weekPercentage,
                    slices: [
                      {start: -30, sweep: weekTicks, color: "green"},
                      {start: -30 + weekTicks, sweep: 360-weekTicks, color: "black"}
                    ]
                  },

                  {
                    key: 4,
                    text: "192.168.4.140\nUPTIME (1 month) - " + monthPercentage,
                    slices: [
                      {start: -30, sweep: monthTicks, color: "green"},
                      {start: -30 + monthTicks, sweep: 360-monthTicks, color: "black"}
                    ]
                  }
                ],
              linkDataArray:
                [  // link data
                  //{from: 1, to: 2}
                ]
            })
        }
      );
  }

  makeGeo(data): any {
    // this is much more efficient than calling go.GraphObject.make:
    return new go.Geometry()
      .add(new go.PathFigure(50, 50)  // start point
        .add(new go.PathSegment(go.PathSegment.Arc,
          data.start, data.sweep,  // angles
          50, 50,  // center
          50, 50)  // radius
          .close()));
  }
}



